/**
 * Module 00 — Global Shell · Navigation model
 *
 * Single source of truth for the sidebar groups (the 10 PRD §0.1 groups plus
 * Apps, the app marketplace). Mirrors the
 * prototype `shell.jsx` `NAV` verbatim (same `route` strings so deep-links and
 * the command palette keep working) and layers on RBAC-by-hiding metadata
 * (Blueprint §4.2): every group carries a `capability` and a couple of items
 * carry an item-level `capability` for fine-grained gating.
 */

export interface NavItem {
  label: string;
  route: string;
  icon: string;
  badge?: string;
  /**
   * FAB-154: one line on what you can do at this destination, shown as a
   * tooltip on the nav item. Says what the section is FOR — the label already
   * names it.
   */
  hint?: string;
  /** Item-level capability gate (e.g. Billing → `billing.view`). */
  capability?: string;
  /** Shows the item, and its group, even when the group's own capability is missing. */
  showsGroup?: boolean;
  /**
   * Shown only to an organization administrator (the same `isOrgAdminRole`
   * check the app marketplace's admin actions use). Withheld until the role is
   * known, and withheld from every caller that does not pass `orgAdmin: true`.
   */
  orgAdminOnly?: boolean;
  children?: NavItem[];
}

/** Role facts that gate nav items beyond capabilities. */
export interface NavFilterOptions {
  /** True only once the signed-in user is known to be an organization administrator. */
  orgAdmin?: boolean;
}

export interface NavGroup {
  group: string;
  /** Group-level capability gate (e.g. `nav.governance`). */
  capability?: string;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    group: 'Workspace',
    capability: 'nav.workspace',
    items: [
      { label: 'Home', route: 'home', icon: 'home', hint: 'Project overview — activity, health and quick starts' },
      { label: 'Documentation', route: 'docs', icon: 'book', hint: 'Guides and API reference for building on Fabric' },
      // { label: 'Do Mode', route: 'do', icon: 'rocket', hint: 'Ask Fabric to carry out a task end to end' }, // FAB-40
      // { label: 'History', route: 'history', icon: 'history', hint: 'Everything Fabric has done in this project' }, // FAB-41
    ],
  },
  {
    group: 'Connect',
    capability: 'nav.connect',
    items: [
      // Module 02 routes live under /connect/* (see useShellNav routeFromPath).
      // AI Models moved to Settings → AI Models (org-level model management).
      // MCP servers are no longer a separate tab — they're surfaced alongside
      // regular connectors in the Connectors Marketplace (Add Connector). The
      // /connect/mcp-servers route stays mounted only as the MCP OAuth landing.
      // Data Sources (the OpenMetadata catalog) is its own sidebar entry so it
      // doesn't disturb the connectors+MCP Marketplace — it opens the standalone
      // /connect/catalog page (CatalogView) to browse and connect data systems.
      { label: 'Connectors', route: 'connect/connectors', icon: 'plug', hint: 'Connect integrations and manage the actions they expose' },
      { label: 'Data Sources', route: 'connect/catalog', icon: 'catalog', hint: 'Browse data sources discovered across your systems' },
    ],
  },
  {
    group: 'Orchestrate',
    capability: 'nav.orchestrate',
    items: [
      { label: 'Agents', route: 'agents', icon: 'agent', hint: 'Build and run AI coworkers that use your skills and data' },
      { label: 'Multi Agent Flows', route: 'process-flows', icon: 'processflow', hint: 'Chain several agents into one flow that hands work along' },
      { label: 'Executions', route: 'executions', icon: 'executions', hint: 'Every agent and flow run, with status and timings' },
      {
        label: 'Skills',
        route: 'skills',
        icon: 'skills',
        children: [
          { label: 'Library', route: 'skills', icon: 'skilllibrary', hint: 'Reusable capabilities your agents can use — not the same thing as a raw connector action' },
          { label: 'Marketplace', route: 'skills-marketplace', icon: 'cards', hint: 'Install skills published by others in your organisation' },
          { label: 'Skill Reviews', route: 'skills-approvals', icon: 'skillreview', hint: 'Decide skills awaiting project activation' },
          // { label: 'Learn Mode', route: 'learn', icon: 'learn', hint: 'Teach Fabric facts it should remember for this project' }, // FAB-54
        ],
      },
    ],
  },
  {
    group: 'Context Graph',
    capability: 'nav.context-graph',
    items: [
      // Module 04 routes live under /context-graph/* (see useShellNav routeFromPath).
      // The VIEWER is open to every holder of nav.context-graph (members included —
      // what they see inside is node-level enforced server-side); the deeper tools
      // are gated by `context.curate` (admins/power users), so a member's group
      // shows exactly the read-only graph.
      { label: 'Context Graph', route: 'context-graph/knowledge-graph', icon: 'knowledgegraph', hint: 'Explore your data as a graph of connected entities' },
      { label: 'Ontology', route: 'context-graph/cognitive', icon: 'ontology', capability: 'context.curate', hint: 'Define the entity types and relationships in your graph' },
      // Lineage is its own module (FAB-55) — data flow / transformation tracking,
      // extracted from the Ontology Studio tabs.
      { label: 'Lineage', route: 'context-graph/lineage', icon: 'lineage', capability: 'context.curate', hint: 'Trace where a piece of data came from and what used it' },
      { label: 'Knowledge Bases', route: 'context-graph/knowledge-base', icon: 'kb', capability: 'context.curate', hint: 'Documents agents can search when answering' },
      { label: 'Tables', route: 'context-graph/tables', icon: 'grid', capability: 'context.curate', hint: 'Structured tables agents can read and write' },
    ],
  },
  {
    group: 'Actions',
    capability: 'nav.actions',
    items: [
      { label: 'Action Registry', route: 'actions/action-registry', icon: 'actions', hint: 'Every action agents can take, with its risk level' },
      // { label: 'Cross-System Mappings', route: 'actions/cross-system-mappings', icon: 'mappings', hint: 'Map fields between systems so actions line up' }, // FAB-72
      { label: 'Simulation & Dry Run', route: 'actions/action-simulation', icon: 'flask', hint: 'Try an action without touching the real system' },
      { label: 'Approvals', route: 'actions/approval-flows', icon: 'approval', hint: 'Decide which actions need a human to sign off' },
    ],
  },
  {
    group: 'Simulate',
    capability: 'nav.simulate',
    items: [
      // Module 06 pages are nested under /simulate/* (see useShellNav
      // routeFromPath), mirroring the Connect convention.
      { label: 'Agent Simulation', route: 'simulate/agent', icon: 'simulation', hint: 'Run an agent against test input to see how it behaves' },
      { label: 'Connector Simulation', route: 'simulate/connector', icon: 'connsim', hint: 'Check a connector responds correctly before agents use it' },
    ],
  },
  {
    group: 'Data Management',
    capability: 'nav.data',
    items: [
      { label: 'Ingestion', route: 'data-management/ingestion', icon: 'ingestion', hint: 'Pull data from your sources into the context graph' },
      // { label: 'Mappings', route: 'data-management/mappings', icon: 'mappings', hint: 'Shape incoming data to match your ontology' }, // FAB-72
    ],
  },
  {
    group: 'Governance',
    capability: 'nav.governance',
    items: [
      { label: 'Overview', route: 'governance', icon: 'govoverview', hint: 'Readiness, enforcement coverage and active policy' },
      { label: 'AI Constitution', route: 'governance/constitution', icon: 'principles', hint: 'Non-overridable principles every agent inherits' },
      { label: 'AI Policy', route: 'governance/ai-policy', icon: 'aipolicy', hint: 'What this organization permits or requires of its agents' },
      { label: 'Enforcement', route: 'governance/enforcement', icon: 'lock', hint: 'Whether governance is enforced, fail-closed and content safety' },
      { label: 'Context Policies', route: 'governance/context-policy', icon: 'datapolicy', hint: 'Which data agents may use, for what purpose and how long' },
      { label: 'Agent Clearance', route: 'governance/agents', icon: 'clearance', hint: 'Which agents may act on outside systems, and until when' },
      { label: 'Guardrails', route: 'governance/guardrails', icon: 'guardrails', hint: 'Limits on what agents may say and do' },
      { label: 'Responsible AI', route: 'governance/responsible-ai', icon: 'responsible', hint: 'Model and connector allowlists, data sensitivity rules and evaluation results' },
      { label: 'Observability', route: 'governance/observability', icon: 'observability', hint: 'Traces, metrics and logs for everything running' },
      { label: 'Audit & Lineage', route: 'governance/audit', icon: 'audit', hint: 'Who did what, when — and what it changed' },
      { label: 'Compliance', route: 'governance/compliance', icon: 'compliance', hint: 'Evidence and controls for your audits' },
    ],
  },
  {
    group: 'Insights',
    capability: 'nav.insights',
    items: [
      // analytics-service workflow studio (own route tree at /analytics, served by
      // the Python analytics-service via the /api/analytics proxy). Mapped in
      // useShellNav (route key 'analytics-studio').
      { label: 'Analytics Studio', route: 'analytics-studio', icon: 'analytics', capability: 'analytics.view', hint: 'Build scheduled analyses over your graph' },
    ],
  },
  {
    // App marketplace: separately licensed products (CodeMesh first) an org
    // administrator installs on this platform or runs from a bundle. identity
    // grants nav.apps to every role; installing is still admin-only server-side.
    // Routes live under /apps/* (see useShellNav routeFromPath). Only Wexa
    // publishes apps, so an installation has no publishing pages.
    group: 'Apps',
    capability: 'nav.apps',
    items: [
      { label: 'Marketplace', route: 'apps', icon: 'appstore', hint: 'Browse apps you can add to your organisation, and request or install them' },
      { label: 'Installed apps', route: 'apps/installed', icon: 'installedapps', hint: 'Apps installed for your organisation, with their status and links' },
    ],
  },
  {
    group: 'Settings',
    capability: 'nav.settings',
    items: [
      { label: 'Org', route: 'settings/org', icon: 'orgsettings', hint: 'Organisation profile, domain and deployment posture' },
      { label: 'Users', route: 'settings/users', icon: 'users', hint: 'Invite people and manage their access' },
      { label: 'Roles', route: 'settings/roles', icon: 'roles', capability: 'roles.manage', hint: 'What each role is allowed to do' },
      // Data-access grants (RBAC data plane): which audiences (a Linear team, a
      // GitHub repo, …) each org/dept/project/user may see. Server enforces the
      // delegation ceiling; the page is for the org's access administrators.
      { label: 'Access Grants', route: 'settings/access-grants', icon: 'grants', capability: 'roles.manage', hint: 'Time-boxed access granted to people or agents' },
      { label: 'Departments', route: 'settings/departments', icon: 'departments', hint: 'Group people into departments for approvals and scope' },
      { label: 'Projects', route: 'settings/projects', icon: 'projects', hint: 'Create and configure projects' },
      { label: 'AI Usage Controls', route: 'settings/policy', icon: 'usagecontrols', hint: 'Which models may be used, on what data, and how far agents may go alone' },
      { label: 'AI Constitution', route: 'settings/constitution', icon: 'constitution', hint: 'The principles every agent must follow' },
      { label: 'Usage & Cost', route: 'settings/usage', icon: 'usage', hint: 'Credit and token spend, broken down by project' },
      { label: 'Developer access', route: 'settings/developer-access', icon: 'apikeys', capability: 'developer.access', showsGroup: true, hint: 'API keys, SDK sessions and allowed origins for this project' },
      { label: 'Billing', route: 'settings/billing', icon: 'billing', capability: 'billing.view', hint: 'Plan, invoices and payment method' },
    ],
  },
];

/** Flattened lookup of every nav item (children included), tagged with its group. */
export interface FlatNavItem extends NavItem {
  group: string;
}

function flatten(nav: NavGroup[]): FlatNavItem[] {
  const flat: FlatNavItem[] = [];
  nav.forEach((g) =>
    g.items.forEach((it) => {
      flat.push({ ...it, group: g.group });
      it.children?.forEach((ch) => flat.push({ ...ch, group: g.group }));
    }),
  );
  return flat;
}

export const FLAT: FlatNavItem[] = flatten(NAV);

/** route → metadata, used by the Placeholder fallback (G-05) and the palette. */
export const META: Record<string, FlatNavItem> = {};
FLAT.forEach((it) => {
  if (!META[it.route]) META[it.route] = it;
});

/**
 * Returns the metadata for a route, or a synthetic entry so unknown routes can
 * still render a wired `<Placeholder>` instead of crashing (G-05).
 */
export function metaForRoute(route: string): { label: string; icon: string; group?: string } {
  return META[route] || { label: route, icon: 'sparkles' };
}

/**
 * RBAC-by-hiding (Blueprint §4.2). Filters groups/items down to what the
 * capability set allows. A `null`/`undefined` capability set means "unfiltered"
 * (e.g. capabilities not yet loaded, or super_admin) — return NAV untouched.
 * Empty groups are dropped so the sidebar never shows a bare group label.
 *
 * `orgAdminOnly` items are dropped unless `options.orgAdmin` is true, whether
 * or not capabilities are known: an unknown role is not an administrator.
 */
export function filterNav(
  nav: NavGroup[] = NAV,
  capabilities?: Set<string> | null,
  options: NavFilterOptions = {},
): NavGroup[] {
  const byRole = options.orgAdmin ? nav : withoutOrgAdminItems(nav);
  if (!capabilities) return byRole;
  const allowed = (it: NavItem) => !it.capability || capabilities.has(it.capability);
  return byRole
    .map((g) => {
      const groupOpen = !g.capability || capabilities.has(g.capability);
      return { g, groupOpen };
    })
    .filter(({ g, groupOpen }) => groupOpen || g.items.some((it) => it.showsGroup && allowed(it)))
    .map(({ g, groupOpen }) => ({
      ...g,
      items: g.items
        .filter((it) => allowed(it) && (groupOpen || it.showsGroup))
        .map((it) =>
          it.children
            ? { ...it, children: it.children.filter((ch) => !ch.capability || capabilities.has(ch.capability)) }
            : it,
        ),
    }))
    .filter((g) => g.items.length > 0);
}

/** Drops `orgAdminOnly` items (and any group left empty); returns `nav` itself when there are none. */
function withoutOrgAdminItems(nav: NavGroup[]): NavGroup[] {
  const hasAny = nav.some((g) => g.items.some((it) => it.orgAdminOnly || it.children?.some((ch) => ch.orgAdminOnly)));
  if (!hasAny) return nav;
  return nav
    .map((g) => ({
      ...g,
      items: g.items
        .filter((it) => !it.orgAdminOnly)
        .map((it) => (it.children ? { ...it, children: it.children.filter((ch) => !ch.orgAdminOnly) } : it)),
    }))
    .filter((g) => g.items.length > 0);
}

/** Flattened, capability-filtered list for the command palette index. */
export function filterFlat(capabilities?: Set<string> | null, options: NavFilterOptions = {}): FlatNavItem[] {
  return flatten(filterNav(NAV, capabilities, options));
}

/** Whether the credits meter + Upgrade should be shown (gated on `billing.view`). */
export function canViewBilling(capabilities?: Set<string> | null): boolean {
  if (!capabilities) return true;
  return capabilities.has('billing.view');
}
